'use client';

import React from 'react';
import { buildFaqJsonLd, serializeJsonLd } from '@/lib/structuredData';

const DEFAULT_PRICE_VALID_UNTIL = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];

/**
 * JSON-LD schema for breadcrumbs
 * @param {Object} props
 * @param {Array} props.items - Array of breadcrumb items {name: string, url: string}
 * @returns {JSX.Element}
 */
export function ESimBreadcrumbSchema({ items }) {
    if (!items || items.length === 0) return null;

    const schema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url
        }))
    };

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
        />
    );
}

/**
 * JSON-LD schema for FAQ
 * @param {Object} props
 * @param {Array} props.faqs - Array of FAQ items {question: string, answer: string}
 * @returns {JSX.Element}
 */
export function ESimFAQSchema({ faqs }) {
    const schema = buildFaqJsonLd(faqs);
    if (!schema) return null;

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
        />
    );
}

/**
 * JSON-LD schema for ItemList (list of eSIM plans)
 * @param {Object} props
 * @param {Array} props.plans - Array of plan objects with provider, dataAmount, dataUnit, priceAmount, priceCurrency, logo, description, url
 * @param {string} props.country - Country name
 * @param {string} props.baseUrl - Base URL for the site (default: https://www.triplinkhub.com)
 * @returns {JSX.Element}
 */
export function ESimListSchema({ plans, country, baseUrl = 'https://www.triplinkhub.com' }) {
    if (!plans || plans.length === 0) return null;

    const schema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": `eSIM Plans for ${country}`,
        "description": `List of eSIM plans available for ${country}`,
        "numberOfItems": plans.length,
        "itemListElement": plans
            .map((plan, index) => {
                // Проверяем, что все необходимые данные есть
                if (!plan.provider) {
                    console.warn(`ESimListSchema: plan at index ${index} missing provider`, plan);
                    return null;
                }

                const providerName = plan.provider || 'eSIM Provider';
                const dataAmount = plan.dataAmount || 'Unlimited';
                const dataUnit = plan.dataUnit || 'GB';
                const productName = `${providerName} - ${dataAmount} ${dataUnit}`;
                const productDescription = plan.description || `eSIM plan with ${dataAmount} ${dataUnit} of data for ${country}`;

                // Формируем полный URL для изображения
                let imageUrl = `${baseUrl}/images/providers/default.svg`;
                if (plan.logo) {
                    if (plan.logo.startsWith('http')) {
                        imageUrl = plan.logo;
                    } else {
                        imageUrl = `${baseUrl}${plan.logo.startsWith('/') ? '' : '/'}${plan.logo}`;
                    }
                }

                // Google требует, чтобы image был массивом или объектом ImageObject
                const imageData = {
                    "@type": "ImageObject",
                    "url": imageUrl,
                    "contentUrl": imageUrl
                };

                // Offer добавляем ТОЛЬКО при реальной положительной цене —
                // иначе Product без offers (валидно), без выдуманной цены.
                const priceNum = Number(plan.priceAmount);
                const hasPrice = Number.isFinite(priceNum) && priceNum > 0;

                const product = {
                    "@type": "Product",
                    "name": productName,
                    "description": productDescription,
                    "image": [imageData], // Массив для совместимости с Google
                    "brand": {
                        "@type": "Brand",
                        "name": providerName
                    }
                };

                if (hasPrice) {
                    product.offers = {
                        "@type": "Offer",
                        "price": priceNum,
                        "priceCurrency": plan.priceCurrency || "EUR",
                        "availability": "https://schema.org/InStock",
                        "url": plan.url || `${baseUrl}/en/${country}/esim`,
                        "priceValidUntil": DEFAULT_PRICE_VALID_UNTIL,
                        "itemCondition": "https://schema.org/NewCondition",
                        "seller": {
                            "@type": "Organization",
                            "name": providerName
                        }
                    };
                }

                return {
                    "@type": "ListItem",
                    "position": index + 1,
                    "item": product
                };
            })
            .filter(item => item !== null) // Убираем null элементы
    };

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
        />
    );
}
